import { ErrorHandler, inject, Injectable, Injector, type Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import {
	catchError,
	combineLatest,
	defer,
	distinctUntilChanged,
	EMPTY,
	filter,
	map,
	merge,
	type Observable,
	of,
	startWith,
	Subject,
	switchMap,
	take,
	tap,
} from 'rxjs';

import { UiFeatureFlagKey } from '@efficiently/feature-flags-core';

import { DesignChangeGatedService, DesignChangeRepository } from '@efficiently/client-api-design-changes';
import { FeatureFlagsService } from '@efficiently/client-core-feature-flags';
import { ProjectDetailSelectors } from '@efficiently/client-domain-project-detail';

const NO_GATED_IDS: ReadonlySet<string> = new Set<string>();

/**
 * A read request. It carries the state it was raised under rather than reading it again later, so a
 * trigger cannot walk past the guard in {@link readFor} on the way there, and it is tagged with
 * whether the set must blank first.
 */
type GatedRead = {
	readonly projectId: string;
	readonly isEnabled: boolean;
	readonly isReset: boolean;
};

/**
 * The gated set follows the pending set's refresh contract, with one input added: the capture flag.
 *
 * | Trigger | Before the read resolves | Read? |
 * |---|---|---|
 * | Flag off, or no project | the empty set | no request at all |
 * | Flag turns on, or project changes while on | the empty set | yes |
 * | `refresh()` while on and a project is loaded | the previous set | yes |
 * | `refresh()` while off | the empty set, unchanged | no |
 * | A read fails | whatever the trigger established | reported once to `ErrorHandler` |
 *
 * **Nothing is read while the flag is off**, and that row is load-bearing twice. The query throws
 * until its server half ships, so a flag-blind read would put an error on every project load; and
 * off has to be phase 1 exactly — a surface that reads an empty set behaves as though the seam were
 * not there.
 *
 * A project change or a flag flip blanks, because project B must never show project A's gates and
 * an off flag must show none. **A refresh must not blank:** it follows a scope reaching a vendor,
 * and blanking there would re-open the ungated path for one round trip — the thing the set exists to
 * close.
 *
 * A failure keeps whatever the trigger already established, which falls out of the same rule: the
 * read reports to `ErrorHandler` and then emits nothing, so the signal holds its last value.
 */
@Injectable()
export class DefaultDesignChangeGatedService extends DesignChangeGatedService {
	private readonly repository = inject(DesignChangeRepository);
	private readonly errorReporter = inject(ErrorHandler);
	private readonly refreshSubject = new Subject<void>();

	private readonly store = inject(Store);
	private readonly injector = inject(Injector);

	/**
	 * Declared `string` by the selector, but the state carries no default — outside a loaded project it
	 * reads `undefined`. Hence the guard below rather than trust in the type.
	 */
	private readonly projectId: Signal<string> = this.store.selectSignal(ProjectDetailSelectors.projectId);

	private readonly isCaptureEnabled: Signal<boolean> = inject(FeatureFlagsService).enabled(
		UiFeatureFlagKey.DesignChangeCapturePhase2,
	);

	private readonly projectId$ = merge(
		of(this.projectId()),
		toObservable(this.projectId, { injector: this.injector }),
	).pipe(distinctUntilChanged());

	private readonly isCaptureEnabled$ = merge(
		of(this.isCaptureEnabled()),
		toObservable(this.isCaptureEnabled, { injector: this.injector }),
	).pipe(distinctUntilChanged());

	/**
	 * The refresh is mapped through the current project and the current flag state, so that a refresh
	 * raised while either is missing cannot reach the transport.
	 */
	private readonly gatedReads$: Observable<GatedRead> = merge(
		combineLatest([this.projectId$, this.isCaptureEnabled$]).pipe(
			map(([projectId, isEnabled]): GatedRead => ({ projectId, isEnabled, isReset: true })),
		),
		this.refreshSubject.pipe(
			map((): GatedRead => ({ projectId: this.projectId(), isEnabled: this.isCaptureEnabled(), isReset: false })),
		),
	);

	private readonly isSettledState = signal(false);

	public override readonly gatedProjectFinishIds: Signal<ReadonlySet<string>> = toSignal(
		this.gatedReads$.pipe(switchMap(gatedRead => this.readFor(gatedRead))),
		{ initialValue: NO_GATED_IDS },
	);

	private readonly isSettled$ = toObservable(this.isSettledState, { injector: this.injector });

	public override whenSettled(): Observable<void> {
		return defer(() => (this.isSettledState() ? of(true) : this.isSettled$.pipe(filter(Boolean)))).pipe(
			take(1),
			map((): void => undefined),
		);
	}

	public override refresh(): void {
		this.refreshSubject.next();
	}

	private readFor({ projectId, isEnabled, isReset }: GatedRead): Observable<ReadonlySet<string>> {
		if (!isEnabled || !projectId) {
			this.isSettledState.set(true);

			return of(NO_GATED_IDS);
		}

		if (isReset) {
			this.isSettledState.set(false);

			return this.readGatedIds().pipe(startWith(NO_GATED_IDS));
		}

		return this.readGatedIds();
	}

	/**
	 * Reports the failure and then emits nothing, so the signal keeps whichever value the trigger left
	 * it on. Returning an empty set here instead would drop gates that are still in force.
	 */
	private readGatedIds(): Observable<ReadonlySet<string>> {
		return this.repository.getGatedProjectFinishIds().pipe(
			map((ids): ReadonlySet<string> => new Set(ids)),
			tap(() => this.isSettledState.set(true)),
			catchError((error: unknown) => {
				this.errorReporter.handleError(error);
				this.isSettledState.set(true);

				return EMPTY;
			}),
		);
	}
}
